import { Injectable } from '@angular/core'
import { Parameter, Product, ProductParameter } from 'depoto-core/src/entities'
import { CoreService } from './core.service'
import { SchemaUtil } from '../utils'

export const MD_CLASS_EXTERNAL_ID = 'hs-mdclass'
export const DEFAULT_MD_CLASSES = ['I', 'IIa', 'IIb', 'III']

export interface MdClassOption {
  id: string
  name: string
}

@Injectable({
  providedIn: 'root',
})
export class ProductMdClassService {
  private cachedOptions: MdClassOption[] = []

  constructor(private readonly core: CoreService) {}

  getMdClassParameter(): Parameter | undefined {
    return this.core.lists.parameters?.find(p => p.externalId === MD_CLASS_EXTERNAL_ID)
  }

  async fetchMdClassParameter(): Promise<Parameter | undefined> {
    let parameter = this.getMdClassParameter()
    if (!parameter) {
      try {
        const result = await this.core.services.parameter.getList(
          { filters: { externalId: MD_CLASS_EXTERNAL_ID } },
          SchemaUtil.parameter,
        )
        if (result?.items?.length > 0) {
          parameter = result.items[0]
          if (this.core.lists.parameters) {
            this.core.lists.parameters.push(parameter)
          }
        }
      } catch (err) {
        console.warn('Failed to load hs-mdclass parameter', err)
      }
    }
    return parameter
  }

  getOptions(): MdClassOption[] {
    const parameter = this.getMdClassParameter()
    const enumValues =
      parameter?.enumValues && parameter.enumValues.length > 0 ? parameter.enumValues : DEFAULT_MD_CLASSES

    if (
      this.cachedOptions.length === enumValues.length + 1 &&
      this.cachedOptions.slice(1).every((opt, idx) => opt.id === enumValues[idx])
    ) {
      return this.cachedOptions
    }

    this.cachedOptions = [{ id: '', name: 'product.mdClass.none' }, ...enumValues.map(val => ({ id: val, name: val }))]
    return this.cachedOptions
  }

  getExistingProductParameter(product: Product): ProductParameter | undefined {
    if (!product?.productParameters?.length) {
      return undefined
    }
    return product.productParameters.find(
      pp => pp.parameter?.externalId === MD_CLASS_EXTERNAL_ID || pp.externalId === MD_CLASS_EXTERNAL_ID,
    )
  }

  getCurrentValue(product: Product): string {
    return this.getExistingProductParameter(product)?.value || ''
  }

  normalizeMdClass(val: any): string {
    if (!val || typeof val !== 'string') {
      return ''
    }
    const trimmed = val.trim()
    const options = this.getOptions()
    const matched = options.find(opt => opt.id.toLowerCase() === trimmed.toLowerCase())
    return matched ? matched.id : trimmed
  }

  async save(product: Product, selectedValue: string): Promise<ProductParameter | null> {
    if (!product?.id) {
      return null
    }

    const existingParam = this.getExistingProductParameter(product)
    const oldValue = existingParam?.value || ''
    const targetValue = this.normalizeMdClass(selectedValue)

    if (oldValue === targetValue) {
      return existingParam || null
    }

    if (!oldValue && targetValue) {
      const parameter = await this.fetchMdClassParameter()
      const newParam: Partial<ProductParameter> = {
        parameter: parameter ? ({ id: parameter.id } as Parameter) : null,
        product: { id: product.id } as Product,
        value: targetValue,
        externalId: MD_CLASS_EXTERNAL_ID,
      }
      const created = await this.core.services.productParameter.create(newParam, SchemaUtil.productParameter)
      if (created) {
        if (!product.productParameters) {
          product.productParameters = []
        }
        product.productParameters.push(created)
      }
      return created
    }

    if (oldValue && targetValue) {
      const parameter = existingParam.parameter?.id ? existingParam.parameter : await this.fetchMdClassParameter()
      const updateParam: Partial<ProductParameter> = {
        id: existingParam.id,
        parameter: parameter ? ({ id: parameter.id } as Parameter) : null,
        product: { id: product.id } as Product,
        value: targetValue,
        externalId: MD_CLASS_EXTERNAL_ID,
      }
      const updated = await this.core.services.productParameter.update(updateParam, SchemaUtil.productParameter)
      if (updated) {
        existingParam.value = targetValue
      }
      return updated
    }

    if (oldValue && !targetValue) {
      await this.core.services.productParameter.delete({ id: existingParam.id })
      if (product.productParameters) {
        product.productParameters = product.productParameters.filter(pp => pp.id !== existingParam.id)
      }
      return null
    }

    return null
  }
}
